import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiArrowLeft, HiTemplate, HiCheck } from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';

// ─── Templates ────────────────────────────────────────────────────────────────

const TEMPLATES = [
  {
    id: 'freelance',
    label: 'Freelance Service Agreement',
    title: 'Freelance Service Agreement',
    content: `FREELANCE SERVICE AGREEMENT

This Freelance Service Agreement ("Agreement") is entered into as of the date signed below, between the freelancer ("Service Provider") and the client ("Client").

1. SERVICES
Service Provider agrees to perform the following services as mutually agreed upon in writing prior to commencement of work. Specific deliverables, timelines, and milestones will be outlined in a separate project scope document.

2. PAYMENT TERMS
Client agrees to pay Service Provider the agreed-upon fees for services rendered. Payment is due within 14 days of invoice date. Late payments are subject to a 1.5% monthly fee.

3. INTELLECTUAL PROPERTY
Upon receipt of full payment, all work product created under this Agreement shall become the property of Client. Service Provider retains the right to display completed work in their portfolio.

4. CONFIDENTIALITY
Both parties agree to keep confidential any proprietary information shared during the course of this engagement and not to disclose such information to third parties.

5. REVISIONS
The project includes up to two (2) rounds of revisions. Additional revision rounds will be billed at the hourly rate agreed upon.

6. TERMINATION
Either party may terminate this Agreement with 14 days written notice. Client shall pay for all work completed up to the termination date.

7. LIMITATION OF LIABILITY
Service Provider's liability under this Agreement shall not exceed the total fees paid by Client for the services giving rise to the claim.

8. GOVERNING LAW
This Agreement shall be governed by the laws of the jurisdiction in which Service Provider operates.

By signing below, both parties agree to the terms and conditions of this Agreement.`,
  },
  {
    id: 'webdesign',
    label: 'Web Design Contract',
    title: 'Web Design Contract',
    content: `WEB DESIGN CONTRACT

This Web Design Contract ("Agreement") is made between the designer ("Designer") and the client ("Client") for the purpose of designing and developing a website.

1. PROJECT SCOPE
Designer agrees to design and develop a website as described in the project proposal. Any features or pages not explicitly outlined in the proposal are considered out of scope and will require a separate agreement.

2. TIMELINE
The project timeline will be established in the project proposal. Delays caused by Client (e.g., late feedback, missing content) may extend the timeline accordingly. Designer will communicate any anticipated delays.

3. CONTENT & ASSETS
Client is responsible for providing all text, images, logos, and other content required for the website. Designer is not liable for any delays resulting from late delivery of assets.

4. PAYMENT SCHEDULE
- 50% deposit due upon signing of this Agreement
- 25% due upon delivery of design mockups for approval
- 25% due upon launch of the completed website

5. REVISIONS
This project includes up to three (3) rounds of design revisions. Additional revisions beyond this limit will be billed at the agreed hourly rate.

6. BROWSER COMPATIBILITY
Designer will ensure the website is compatible with the latest versions of major browsers (Chrome, Firefox, Safari, Edge). Compatibility with older browser versions is not guaranteed unless specified.

7. LAUNCH & HANDOVER
Upon final payment, Designer will transfer all website files and credentials to Client. Ongoing maintenance and hosting are not included unless specified in a separate agreement.

8. INTELLECTUAL PROPERTY
Upon receipt of full payment, all design work and code produced under this Agreement becomes the property of Client. Designer retains the right to display the work in their portfolio.

9. CONFIDENTIALITY
Both parties agree to maintain the confidentiality of any proprietary information exchanged during this project.

By signing below, both parties agree to the terms set forth in this Web Design Contract.`,
  },
  {
    id: 'consulting',
    label: 'Consulting Agreement',
    title: 'Consulting Agreement',
    content: `CONSULTING AGREEMENT

This Consulting Agreement ("Agreement") is entered into between the consultant ("Consultant") and the client ("Client") for the provision of consulting services.

1. SCOPE OF CONSULTING SERVICES
Consultant agrees to provide advisory and consulting services as mutually defined. Services may include strategic planning, process optimization, technical guidance, or other areas of expertise as agreed in writing.

2. TERM
This Agreement shall commence on the date of signing and continue until the agreed project completion date or until terminated by either party as outlined in Section 7.

3. COMPENSATION
Client agrees to compensate Consultant at the rate and schedule specified in the project proposal. All fees are exclusive of applicable taxes, which are the responsibility of Client.

4. EXPENSES
Reasonable out-of-pocket expenses incurred by Consultant in the performance of services (travel, materials, etc.) shall be reimbursed by Client upon submission of receipts, if pre-approved in writing.

5. INDEPENDENT CONTRACTOR
Consultant is an independent contractor and not an employee of Client. Consultant is responsible for all applicable taxes and is not entitled to employee benefits.

6. CONFIDENTIALITY & NON-DISCLOSURE
Consultant agrees to hold in strict confidence all information, data, and materials shared by Client and shall not disclose such information to third parties without Client's prior written consent.

7. TERMINATION
Either party may terminate this Agreement with 30 days written notice. Client shall pay for all services rendered up to the termination date.

8. CONFLICT OF INTEREST
Consultant agrees to disclose any potential conflicts of interest prior to commencing work and throughout the term of this Agreement.

9. LIMITATION OF LIABILITY
Consultant's total liability under this Agreement shall not exceed the total fees paid by Client in the three months preceding the claim.

10. GOVERNING LAW
This Agreement is governed by the applicable laws of the jurisdiction in which services are performed.

By signing below, both parties acknowledge and agree to the terms of this Consulting Agreement.`,
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function inputCls(hasError) {
  return [
    'w-full px-3 py-2.5 border rounded-xl text-sm text-gray-900 placeholder-gray-400',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition',
    hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300',
  ].join(' ');
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      {title && <h3 className="text-sm font-semibold text-gray-900 mb-5">{title}</h3>}
      {children}
    </div>
  );
}

function FormLabel({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function ContractCreate() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const { id }     = useParams();
  const isEdit     = !!id;

  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);

  // Form state
  const [clientId, setClientId] = useState('');
  const [title,    setTitle]    = useState('');
  const [content,  setContent]  = useState('');
  const [errors,   setErrors]   = useState({});

  // ── Load ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      setLoading(true);

      const { data: clientData } = await supabase
        .from('clients')
        .select('id, name, email')
        .eq('user_id', user.id)
        .eq('status', 'Active')
        .order('name');
      setClients(clientData ?? []);

      if (isEdit) {
        const { data: contract, error } = await supabase
          .from('contracts')
          .select('*')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();

        if (error || !contract) {
          toast.error('Contract not found');
          navigate('/contracts');
          return;
        }
        setClientId(contract.client_id ?? '');
        setTitle(contract.title ?? '');
        setContent(contract.content ?? '');
      }

      setLoading(false);
    };

    init();
  }, [user.id, id, isEdit, navigate]);

  // ── Template picker ────────────────────────────────────────────────────────

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setContent(tpl.content);
    setErrors((p) => ({ ...p, title: '', content: '' }));
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = () => {
    const errs = {};
    if (!clientId)       errs.clientId = 'Select a client';
    if (!title.trim())   errs.title    = 'Title is required';
    if (!content.trim()) errs.content  = 'Contract content is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const save = async (status) => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      user_id:   user.id,
      client_id: clientId,
      title:     title.trim(),
      content:   content.trim(),
      status,
    };

    const { error } = isEdit
      ? await supabase.from('contracts').update(payload).eq('id', id).eq('user_id', user.id)
      : await supabase.from('contracts').insert([payload]);

    if (error) {
      toast.error(error.message);
    } else {
      const msg = isEdit
        ? 'Contract updated!'
        : status === 'Draft' ? 'Draft saved!' : 'Contract sent!';
      toast.success(msg);

      // Auto-send contract notification email when status is Sent
      if (status === 'Sent') {
        const chosenClient = clients.find((c) => c.id === clientId);
        if (chosenClient?.email) {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            const emailRes = await fetch('/api/email/contract-sent', {
              method:  'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization:  `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                clientEmail:   chosenClient.email,
                clientName:    chosenClient.name,
                contractTitle: title.trim(),
              }),
            });
            if (emailRes.ok) {
              toast.success('Email sent to client!');
            } else {
              toast.error('Contract saved but email failed — check your Resend key in Settings → Emails');
            }
          } catch {
            toast.error('Contract saved but email could not be sent');
          }
        }
      }

      navigate('/contracts');
    }
    setSaving(false);
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout title={isEdit ? 'Edit Contract' : 'New Contract'}>
        <div className="flex items-center justify-center py-32">
          <svg className="animate-spin h-8 w-8 text-indigo-500" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      </Layout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title={isEdit ? 'Edit Contract' : 'New Contract'}>

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/contracts')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
        >
          <HiArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Edit Contract' : 'New Contract'}
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {isEdit ? 'Update the contract details below' : 'Fill in details or use a template'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">

        {/* ── Main column ── */}
        <div className="xl:col-span-2 space-y-5">

          {/* Templates — only on create */}
          {!isEdit && (
            <SectionCard title="Start from a Template">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {TEMPLATES.map((tpl) => {
                  const active = title === tpl.title && content === tpl.content;
                  return (
                    <button
                      key={tpl.id}
                      onClick={() => applyTemplate(tpl)}
                      className={`relative flex flex-col items-start gap-1.5 p-4 rounded-xl border text-left
                        transition text-sm font-medium
                        ${active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50'
                        }`}
                    >
                      <HiTemplate className={`w-5 h-5 ${active ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <span className="leading-snug">{tpl.label}</span>
                      {active && (
                        <span className="absolute top-2.5 right-2.5 bg-indigo-500 text-white rounded-full p-0.5">
                          <HiCheck className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Selecting a template pre-fills the title and content. You can edit everything below.
              </p>
            </SectionCard>
          )}

          {/* Contract Details */}
          <SectionCard title="Contract Details">
            <div className="space-y-4">

              {/* Client */}
              <div>
                <FormLabel required>Client</FormLabel>
                <select
                  value={clientId}
                  onChange={(e) => { setClientId(e.target.value); setErrors((p) => ({ ...p, clientId: '' })); }}
                  className={inputCls(errors.clientId)}
                >
                  <option value="">— Select a client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.clientId && <p className="mt-1.5 text-xs text-red-500">{errors.clientId}</p>}
                {clients.length === 0 && (
                  <p className="mt-1.5 text-xs text-amber-600">
                    No active clients yet —{' '}
                    <button onClick={() => navigate('/clients')} className="underline font-medium">
                      add one first
                    </button>
                  </p>
                )}
              </div>

              {/* Title */}
              <div>
                <FormLabel required>Contract Title</FormLabel>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: '' })); }}
                  placeholder="e.g. Freelance Service Agreement"
                  className={inputCls(errors.title)}
                />
                {errors.title && <p className="mt-1.5 text-xs text-red-500">{errors.title}</p>}
              </div>
            </div>
          </SectionCard>

          {/* Contract Content */}
          <SectionCard title="Contract Content">
            <textarea
              value={content}
              onChange={(e) => { setContent(e.target.value); setErrors((p) => ({ ...p, content: '' })); }}
              rows={22}
              placeholder="Enter the full contract text here, or select a template above to get started…"
              className={`w-full px-3 py-3 border rounded-xl text-sm text-gray-900 placeholder-gray-400
                font-mono leading-relaxed resize-y
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition
                ${errors.content ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
            />
            {errors.content && <p className="mt-1.5 text-xs text-red-500">{errors.content}</p>}
            <p className="text-xs text-gray-400 mt-2">
              {content.length} characters · {content.trim().split(/\s+/).filter(Boolean).length} words
            </p>
          </SectionCard>
        </div>

        {/* ── Sidebar ── */}
        <div className="space-y-4 xl:sticky xl:top-6">

          {/* Summary */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 flex-shrink-0">Client</span>
                <span className="font-medium text-gray-900 text-right truncate">
                  {clients.find((c) => c.id === clientId)?.name ?? '—'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 flex-shrink-0">Title</span>
                <span className="font-medium text-gray-900 text-right truncate">
                  {title || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-gray-500 flex-shrink-0">Words</span>
                <span className="font-medium text-gray-900">
                  {content.trim().split(/\s+/).filter(Boolean).length}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2.5">
            <button
              onClick={() => save('Sent')}
              disabled={saving}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3
                rounded-xl transition shadow-sm disabled:opacity-60 text-sm"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Saving…
                </span>
              ) : isEdit ? 'Save Changes' : 'Send to Client'}
            </button>
            {!isEdit && (
              <button
                onClick={() => save('Draft')}
                disabled={saving}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3
                  rounded-xl transition text-sm disabled:opacity-60"
              >
                Save as Draft
              </button>
            )}
            <button
              onClick={() => navigate('/contracts')}
              className="w-full text-gray-400 hover:text-gray-600 font-medium py-2 text-sm transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
